import { singleton } from 'aurelia';

// Owns small, cross-cutting presentational state that is not tied to a single feature:
// the image lightbox, the legal content modal (Terms/Privacy/Disclaimer), and the footer's
// current year. Kept here so the SPA stays self-contained (no extra routes/pages).
@singleton()
export class UiService {
  readonly currentYear: number = new Date().getFullYear();

  // Image lightbox. openLightbox is an arrow property so it can be passed as a callback
  // to child components without losing `this`.
  lightboxImage: string | null = null;

  openLightbox = (imageUrl: string): void => {
    this.lightboxImage = imageUrl;
  };

  closeLightbox(): void {
    this.lightboxImage = null;
  }

  // Legal content modal (Terms, Privacy, Disclaimer).
  legalOpen: boolean = false;
  legalTitle: string = '';
  legalBody: string = '';

  openLegal(topic: 'terms' | 'privacy' | 'disclaimer'): void {
    const doc = UiService.legalDocuments[topic];
    if (!doc) return;
    this.legalTitle = doc.title;
    this.legalBody = doc.body;
    this.legalOpen = true;
  }

  closeLegal(): void {
    this.legalOpen = false;
  }

  // Static legal copy rendered inside the footer's modal. Kept here so the SPA stays
  // self-contained (no extra routes/pages). The <a> targets open safely in a new tab.
  private static readonly legalDocuments: Record<'terms' | 'privacy' | 'disclaimer', { title: string; body: string }> = {
    terms: {
      title: 'Terms & Conditions',
      body: `
        <p>By accessing or using Toy Calendar (the &ldquo;Service&rdquo;), you agree to these Terms &amp; Conditions. If you do not agree, please do not use the Service.</p>
        <h4>Use of the Service</h4>
        <p>The Service is provided for personal, non-commercial informational purposes only. Release dates, prices, and product details are aggregated from third-party sources and may be inaccurate, incomplete, or out of date.</p>
        <h4>No Warranty</h4>
        <p>The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind, whether express or implied. We do not guarantee that the Service will be uninterrupted, timely, secure, or error-free.</p>
        <h4>Limitation of Liability</h4>
        <p>To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, or consequential damages arising from your use of, or inability to use, the Service.</p>
        <h4>Third-Party Content</h4>
        <p>Links and data from external retailers and manufacturers are provided for convenience. We are not responsible for the content, availability, or accuracy of any third-party site or listing.</p>
        <h4>Changes</h4>
        <p>We may update these Terms at any time. Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.</p>
        <p class="legal-modal__full-link"><a href="/terms-and-conditions.html" target="_blank" rel="noopener">Read the full Terms &amp; Conditions</a></p>
      `,
    },
    privacy: {
      title: 'Privacy Policy',
      body: `
        <p>This Privacy Policy explains how Toy Calendar handles information when you use the Service.</p>
        <h4>Information We Collect</h4>
        <p>The Service is designed to minimize data collection. We do not require an account to browse the calendar, and we do not sell personal information. Standard server logs may record technical details such as IP address, browser type, and requested pages for security and diagnostics.</p>
        <h4>Cookies &amp; Local Storage</h4>
        <p>The Service may use essential cookies or browser storage to keep the interface working (for example, to remember an administrator session). No advertising or third-party tracking cookies are used.</p>
        <h4>Third-Party Data</h4>
        <p>Product images and details are retrieved from third-party retailers and manufacturers. Visiting those external links is subject to the respective third party's own privacy practices.</p>
        <h4>Data Retention</h4>
        <p>Technical logs are retained only as long as necessary for operating and securing the Service.</p>
        <h4>Contact</h4>
        <p>For privacy questions, please contact the site administrator.</p>
        <p class="legal-modal__full-link"><a href="/privacy-policy.html" target="_blank" rel="noopener">Read the full Privacy Policy</a></p>
      `,
    },
    disclaimer: {
      title: 'Disclaimer',
      body: `
        <p>Toy Calendar is an independent, non-commercial project. It is not affiliated with, endorsed by, or sponsored by any toy manufacturer, retailer, or brand referenced on the Service.</p>
        <p>All product names, logos, images, and trademarks are the property of their respective owners and are used here for identification and informational purposes only.</p>
        <p>Release dates and prices are estimates aggregated from public sources and are subject to change without notice. Always confirm details with the official retailer before making a purchase.</p>
      `,
    },
  };
}
